import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Users, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { BoostThisPanelCard } from './BoostThisPanelCard'
import { DEMO_TWIN_PANEL_READINESS, DEMO_SEGMENTS, DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'

interface TwinPanelReadiness {
  status?: string
  total_twins: number
  twins_validated: number
  twins_low_confidence: number
  twins_excluded: number
  mean_accuracy: number
  wilson_lower: number
  wilson_upper: number
  p25_accuracy: number
  p50_accuracy: number
  p75_accuracy: number
  p90_accuracy: number
  per_segment_accuracy_json?: string
}

interface BaselineAccuracy {
  random_baseline: number
  modal_baseline: number
  echosome_accuracy: number
  echosome_accuracy_lower: number
  echosome_accuracy_upper: number
  lift_vs_random: number
  lift_vs_modal: number
}

interface BoostTarget {
  segment_id: string
  segment_name: string
  low_confidence_count: number
  suggested_configs: number
  estimated_seconds: number
  estimated_cost_usd: number
}

// Calibration table — Build 23 Part D (CAL-03): per-project holdout accuracy by confidence band
interface Calibration {
  status?: string
  high_total: number
  high_correct: number
  high_accuracy: number
  medium_total: number
  medium_correct: number
  medium_accuracy: number
  low_total: number
  low_correct: number
  low_accuracy: number
  is_monotonic: number | boolean
  calibration_gate_passes: number | boolean
}

const DEMO_CALIBRATION: Calibration = {
  high_total: 412, high_correct: 343, high_accuracy: 0.83,
  medium_total: 268, medium_correct: 174, medium_accuracy: 0.65,
  low_total: 141, low_correct: 59, low_accuracy: 0.42,
  is_monotonic: 1, calibration_gate_passes: 1,
}

const DEMO_BASELINES: BaselineAccuracy = {
  random_baseline: 0.33,
  modal_baseline: 0.52,
  echosome_accuracy: 0.73,
  echosome_accuracy_lower: 0.71,
  echosome_accuracy_upper: 0.75,
  lift_vs_random: 0.40,
  lift_vs_modal: 0.21,
}

const DEMO_BOOST_TARGETS: BoostTarget[] = [
  { segment_id: 'seg_opt_outs', segment_name: 'Recent opt-outs', low_confidence_count: 12, suggested_configs: 4, estimated_seconds: 90, estimated_cost_usd: 0.30 },
  { segment_id: 'seg_high_prem', segment_name: 'High-premium-increase', low_confidence_count: 8, suggested_configs: 3, estimated_seconds: 60, estimated_cost_usd: 0.20 },
]

interface SegmentAccuracy {
  segment_id: string
  name: string
  respondent_count: number
  accuracy: number
  wilson_lower: number
  wilson_upper: number
}

const HISTOGRAM_BUCKETS = [
  { label: '30–40%', min: 0.30, max: 0.40 },
  { label: '40–50%', min: 0.40, max: 0.50 },
  { label: '50–60%', min: 0.50, max: 0.60 },
  { label: '60–70%', min: 0.60, max: 0.70 },
  { label: '70–80%', min: 0.70, max: 0.80 },
  { label: '80–90%', min: 0.80, max: 0.90 },
  { label: '90–100%', min: 0.90, max: 1.01 },
]

function HistogramBar({ fraction, label, isMedian }: { fraction: number; label: string; isMedian: boolean }) {
  const barHeight = Math.max(4, Math.round(fraction * 80))
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative flex items-end" style={{ height: 88 }}>
        <div
          className={`w-8 rounded-t transition-all ${isMedian ? 'bg-echo-indigo' : 'bg-echo-indigo/30'}`}
          style={{ height: barHeight }}
        />
        {isMedian && (
          <span className="absolute -top-5 text-xs text-echo-indigo font-medium whitespace-nowrap">median</span>
        )}
      </div>
      <span className="text-xs text-slate-400 rotate-45 origin-left mt-1">{label}</span>
    </div>
  )
}

interface BuildProgress {
  is_building: boolean
  chunks_done: number
  chunks_total: number
  pct: number
}

export function TwinPanelOverviewPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [readiness, setReadiness] = useState<TwinPanelReadiness | null>(null)
  const [segments, setSegments] = useState<SegmentAccuracy[]>([])
  const [baselines, setBaselines] = useState<BaselineAccuracy | null>(null)
  const [boostTargets, setBoostTargets] = useState<BoostTarget[]>([])
  const [calibration, setCalibration] = useState<Calibration | null>(null)
  const [loading, setLoading] = useState(true)
  const [excludedExpanded, setExcludedExpanded] = useState(false)
  const [whichSurfaceExpanded, setWhichSurfaceExpanded] = useState(false)
  const [baselineExpanded, setBaselineExpanded] = useState(false)
  const [latestRunId, setLatestRunId] = useState<string | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const [buildProgress, setBuildProgress] = useState<BuildProgress | null>(null)
  const progressPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  const fetchBuildProgress = async () => {
    if (!projectId) return
    try {
      const r = await fetch(`/api/v1/projects/${projectId}/twin-panel/build-progress`, { headers })
      if (!r.ok) return
      const data = await r.json() as BuildProgress
      setBuildProgress(data)
      if (!data.is_building) {
        if (progressPollRef.current) clearInterval(progressPollRef.current)
        // Reload readiness once building finishes
        const r2 = await fetch(`/api/v1/projects/${projectId}/twin-panel/readiness`, { headers })
        if (r2.ok) setReadiness(await r2.json() as TwinPanelReadiness)
      }
    } catch { /* ignore */ }
  }

  useEffect(() => {
    if (isDemo) {
      setReadiness(DEMO_TWIN_PANEL_READINESS)
      setSegments(DEMO_SEGMENTS.map(s => ({
        segment_id: s.segment_id,
        name: s.name,
        respondent_count: s.respondent_count,
        accuracy: s.accuracy ?? 0,
        wilson_lower: s.wilson_lower ?? 0,
        wilson_upper: s.wilson_upper ?? 0,
      })))
      setBaselines(DEMO_BASELINES)
      setBoostTargets(DEMO_BOOST_TARGETS)
      setCalibration(DEMO_CALIBRATION)
      setLoading(false)
      return
    }
    Promise.all([
      fetch(`/api/v1/projects/${projectId}/twin-panel/readiness`, { headers })
        .then(r => r.ok ? r.json() as Promise<TwinPanelReadiness> : null)
        .catch(() => null),
      fetch(`/api/v1/projects/${projectId}/twin-panel/segments`, { headers })
        .then(r => r.ok ? r.json() as Promise<{ segments: SegmentAccuracy[] }> : { segments: [] })
        .catch(() => ({ segments: [] })),
      fetch(`/api/v1/projects/${projectId}/twin-panel/baselines`, { headers })
        .then(r => r.ok ? r.json() as Promise<BaselineAccuracy> : null)
        .catch(() => null),
      fetch(`/api/v1/projects/${projectId}/twin-panel/boost-targets`, { headers })
        .then(r => r.ok ? r.json() as Promise<{ targets: BoostTarget[] }> : { targets: [] })
        .catch(() => ({ targets: [] })),
      fetch(`/api/v1/projects/${projectId}/pipeline-runs/latest`, { headers })
        .then(r => r.ok ? r.json() as Promise<{ pipeline_run_id: string }> : null)
        .catch(() => null),
      fetch(`/api/v1/projects/${projectId}/twin-panel/build-progress`, { headers })
        .then(r => r.ok ? r.json() as Promise<BuildProgress> : null)
        .catch(() => null),
      fetch(`/api/v1/projects/${projectId}/twin-panel/calibration`, { headers })
        .then(r => r.ok ? r.json() as Promise<Calibration> : null)
        .catch(() => null),
    ]).then(([r, s, b, bt, run, prog, cal]) => {
      setReadiness(r)
      setSegments(s.segments ?? [])
      if (b) setBaselines(b)
      setBoostTargets(bt.targets ?? [])
      if (cal && cal.status !== 'not_computed' && cal.high_total != null) setCalibration(cal)
      if (run?.pipeline_run_id) setLatestRunId(run.pipeline_run_id)
      if (prog) {
        setBuildProgress(prog)
        if (prog.is_building) {
          progressPollRef.current = setInterval(fetchBuildProgress, 5000)
        }
      }
    }).catch(() => {}).finally(() => setLoading(false))
    return () => { if (progressPollRef.current) clearInterval(progressPollRef.current) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isDemo])

  const handleRetryPipeline = async () => {
    if (!latestRunId || !projectId) return
    setIsRetrying(true)
    try {
      const res = await fetch(`/api/v1/pipeline-runs/${latestRunId}/retry`, { method: 'POST', headers })
      if (res.ok) {
        const data = await res.json() as { pipeline_run_id: string }
        window.location.href = `/app/onboarding/processing?pipeline_run_id=${data.pipeline_run_id}&project_id=${projectId}`
      }
    } catch { /* ignore */ } finally {
      setIsRetrying(false)
    }
  }

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading twin panel...</div>
  if (!readiness || readiness.status === 'not_built' || !readiness.total_twins) {
    const isBuilding = buildProgress?.is_building ?? false
    const pct = buildProgress?.pct ?? 0
    const chunksDone = buildProgress?.chunks_done ?? 0
    const chunksTotal = buildProgress?.chunks_total ?? 0
    return (
      <div className="p-8 space-y-4 max-w-lg">
        {isBuilding ? (
          <>
            <Badge variant="info" size="sm">Building twin panel</Badge>
            <p className="text-sm text-slate-600">
              Your twin panel is being built right now. This page will update automatically when it&apos;s ready.
            </p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-slate-500">
                <span>{chunksDone} of {chunksTotal} batches complete</span>
                <span>{pct}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-echo-indigo h-2 rounded-full transition-all duration-700"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <Badge variant="warning" size="sm">Twin panel not ready</Badge>
            <p className="text-sm text-slate-600">
              Your twin panel hasn&apos;t been built yet. This usually means the pipeline is still processing your uploaded data, or the upload was incomplete.
            </p>
            <div className="flex gap-3 flex-wrap">
              {latestRunId && (
                <Button variant="primary" size="sm" onClick={handleRetryPipeline} disabled={isRetrying}>
                  {isRetrying ? 'Starting…' : 'Retry pipeline'}
                </Button>
              )}
              <Link to={`/app/projects/${projectId}/upload`}>
                <Button variant="outline" size="sm">Re-upload file &rarr;</Button>
              </Link>
            </div>
          </>
        )}
      </div>
    )
  }

  // Compute histogram fractions (demo: distribute across buckets based on p-values)
  const histogramData = HISTOGRAM_BUCKETS.map((bucket, i) => {
    const normalDistApprox = [0.02, 0.05, 0.10, 0.25, 0.32, 0.18, 0.08][i] ?? 0
    const isMedian = readiness.p50_accuracy >= bucket.min && readiness.p50_accuracy < bucket.max
    return { ...bucket, fraction: normalDistApprox, isMedian }
  })

  const pct = (n: number) => `${(n * 100).toFixed(0)}%`

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Badge variant="success" size="sm">Twin Panel ready</Badge>
        <span>·</span>
        <span>{readiness.total_twins.toLocaleString()} twins</span>
        <span>·</span>
        <span>8 configs ran</span>
      </div>

      {/* Headline accuracy */}
      <Card className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500 mb-1">Mean accuracy across validated twins</p>
            <p
              className="text-5xl font-medium text-deep-ink"
              data-testid="twin-panel-headline"
              style={{ letterSpacing: '-0.03em' }}
            >
              {pct(readiness.mean_accuracy)}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Wilson 95% confidence range: {pct(readiness.wilson_lower)}–{pct(readiness.wilson_upper)}
              &nbsp;·&nbsp;
              Top 20% of twins: {pct(readiness.p90_accuracy)} accuracy
            </p>
          </div>
          <div className="shrink-0 w-10 h-10 bg-echo-indigo/10 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-echo-indigo" strokeWidth={1.75} />
          </div>
        </div>

        {/* Histogram */}
        <div>
          <p className="text-xs text-slate-400 mb-3">Distribution of twin accuracy (per 10-point bucket)</p>
          <div className="flex items-end gap-2 overflow-x-auto pb-6">
            {histogramData.map(b => (
              <HistogramBar key={b.label} fraction={b.fraction} label={b.label} isMedian={b.isMedian} />
            ))}
          </div>
        </div>

        {/* Status counts */}
        <div className="flex flex-wrap gap-4 pt-2 border-t border-border-gray">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-validation-green" />
            <span className="text-sm text-slate-700">{readiness.twins_validated.toLocaleString()} validated</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-uncertainty-amber" />
            <span className="text-sm text-slate-700">{readiness.twins_low_confidence.toLocaleString()} low confidence</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-slate-300" />
            <span className="text-sm text-slate-700">{readiness.twins_excluded.toLocaleString()} excluded</span>
          </div>
        </div>
      </Card>

      {/* Baseline-lift card — Part A.2 */}
      {baselines && (
        <Card data-testid="baseline-lift-card" className="space-y-4">
          <h2 className="text-sm font-medium text-deep-ink">How does this compare?</h2>
          <div className="space-y-2.5">
            {[
              { label: 'Random baseline', value: baselines.random_baseline, testId: 'baseline-random' },
              { label: 'Modal baseline', value: baselines.modal_baseline, testId: 'baseline-modal' },
              { label: 'Echosome panel', value: baselines.echosome_accuracy, testId: 'baseline-echosome', highlight: true },
            ].map(bar => (
              <div key={bar.testId} className="flex items-center gap-3" data-testid={bar.testId}>
                <span className="text-sm text-slate-600 w-40 shrink-0">
                  {bar.label} <span className="text-slate-400">({pct(bar.value)})</span>
                </span>
                <div className="flex-1 bg-soft-panel rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${bar.highlight ? 'bg-echo-indigo' : 'bg-slate-300'}`}
                    style={{ width: `${(bar.value * 100).toFixed(1)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 pt-1 border-t border-border-gray text-sm">
            <span className="text-deep-ink font-medium">
              +{Math.round(baselines.lift_vs_random * 100)} points above random
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-deep-ink font-medium">
              +{Math.round(baselines.lift_vs_modal * 100)} points above modal
            </span>
          </div>
          <div className="border border-border-gray rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-3 py-2.5 text-xs text-slate-500 hover:bg-soft-panel/30 transition-colors"
              onClick={() => setBaselineExpanded(!baselineExpanded)}
            >
              <span className="flex items-center gap-1.5">
                <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
                Why these baselines?
              </span>
              {baselineExpanded ? <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.75} /> : <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />}
            </button>
            {baselineExpanded && (
              <div className="px-3 pb-3 text-xs text-slate-600 space-y-1.5 border-t border-border-gray">
                <p className="pt-2"><strong>Random baseline</strong> — accuracy if you guessed randomly from the available options (1 ÷ number of answer choices).</p>
                <p><strong>Modal baseline</strong> — accuracy if you always predicted the most common answer for each question. This is the "lazy" benchmark.</p>
                <p><strong>Echosome panel</strong> — actual ensemble accuracy measured against responses the model never saw during training (held-out backtest).</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Calibration card — Build 23 Part D (CAL-03): the headline trust metric */}
      {calibration && calibration.high_total + calibration.medium_total + calibration.low_total > 0 && (
        <Card data-testid="calibration-card" className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-medium text-deep-ink">Is the confidence trustworthy?</h2>
              <p
                className="text-3xl font-medium text-deep-ink mt-1"
                data-testid="calibration-high-accuracy"
                style={{ letterSpacing: '-0.02em' }}
              >
                {calibration.high_total > 0
                  ? `High-confidence predictions were correct ${pct(calibration.high_accuracy)} of the time`
                  : 'No high-confidence predictions yet'}
              </p>
              {calibration.high_total > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Measured on {calibration.high_total.toLocaleString()} held-out predictions the model marked ≥80% confident.
                </p>
              )}
            </div>
            <Badge
              variant={calibration.calibration_gate_passes ? 'success' : 'warning'}
              size="sm"
            >
              {calibration.calibration_gate_passes ? 'Calibrated' : 'Under review'}
            </Badge>
          </div>

          {/* Band breakdown — proves monotonicity */}
          <div className="space-y-2.5">
            {[
              { label: 'High confidence (≥80%)', acc: calibration.high_accuracy, total: calibration.high_total, testId: 'cal-band-high', highlight: true }, // B19-allowlist: ≥80% is a confidence-band threshold, not an accuracy claim; accuracy rendered via pct()
              { label: 'Medium confidence (55–79%)', acc: calibration.medium_accuracy, total: calibration.medium_total, testId: 'cal-band-medium' }, // B19-allowlist: confidence-band threshold, not accuracy claim
              { label: 'Low confidence (<55%)', acc: calibration.low_accuracy, total: calibration.low_total, testId: 'cal-band-low' }, // B19-allowlist: confidence-band threshold, not accuracy claim
            ].map(band => (
              <div key={band.testId} className="flex items-center gap-3" data-testid={band.testId}>
                <span className="text-sm text-slate-600 w-52 shrink-0">
                  {band.label}{' '}
                  <span className="text-slate-400">
                    ({band.total > 0 ? pct(band.acc) : 'n/a'})
                  </span>
                </span>
                <div className="flex-1 bg-soft-panel rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${band.highlight ? 'bg-echo-indigo' : 'bg-slate-300'}`}
                    style={{ width: `${(band.total > 0 ? band.acc * 100 : 0).toFixed(1)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-500 pt-1 border-t border-border-gray">
            Confidence is the weighted agreement of the configurations that are historically most accurate for each
            respondent — not a model self-rating. Higher-confidence answers are correct more often, which is what
            makes the bands above monotonic.
          </p>

          {/* Regulated-industry disclaimer — required wherever accuracy claims appear */}
          <p className="text-xs text-slate-400">
            Decision support only. Echosome surfaces directional signals from your data. Final decisions stay with your legal, compliance, medical, or regulatory team.
          </p>
        </Card>
      )}

      {/* Primary CTA + secondary actions */}
      <div className="flex flex-col sm:flex-row gap-3" data-testid="primary-cta-zone">
        <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/queries/new`}>
          <Button variant="primary" size="lg" data-testid="primary-cta">Run a bulk query</Button>
        </Link>
        <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/twins`}>
          <Button variant="outline" size="lg">Inspect individual twins</Button>
        </Link>
        <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/accuracy`}>
          <Button variant="ghost" size="lg">Read the calibration report</Button>
        </Link>
        <Link to={`/app/projects/${projectId ?? 'demo'}/twin-panel/accuracy/misses`}>
          <Button variant="ghost" size="lg">Browse backtest misses</Button>
        </Link>
      </div>

      {/* Per-segment accuracy table */}
      {segments.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-medium text-deep-ink">Per-segment accuracy</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray">
                  <th className="text-left py-2 pr-6 font-medium text-slate-500">Segment</th>
                  <th className="text-right py-2 pr-4 font-medium text-slate-500">Twins</th>
                  <th className="text-right py-2 pr-4 font-medium text-slate-500">Mean accuracy</th>
                  <th className="text-right py-2 font-medium text-slate-500">95% confidence range</th>
                </tr>
              </thead>
              <tbody>
                {segments.map(seg => (
                  <tr key={seg.segment_id} className="border-b border-border-gray/40">
                    <td className="py-2 pr-6 text-slate-700">{seg.name ?? seg.segment_id}</td>
                    <td className="text-right py-2 pr-4 text-slate-500">{seg.respondent_count ?? '—'}</td>
                    <td className="text-right py-2 pr-4 font-medium text-deep-ink">{seg.accuracy != null ? pct(seg.accuracy) : '—'}</td>
                    <td className="text-right py-2 text-slate-500 font-mono text-xs">
                      {seg.wilson_lower != null && seg.wilson_upper != null
                        ? `${pct(seg.wilson_lower)}–${pct(seg.wilson_upper)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Boost suggestions — Part C.6 */}
      {boostTargets.length > 0 && (
        <BoostThisPanelCard
          targets={boostTargets}
          projectId={projectId ?? 'demo'}
          onBoostComplete={() => setBoostTargets([])}
        />
      )}

      {/* Why some twins were excluded */}
      <div className="border border-border-gray rounded-xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-deep-ink hover:bg-soft-panel/50 transition-colors"
          onClick={() => setExcludedExpanded(!excludedExpanded)}
          aria-expanded={excludedExpanded}
        >
          <span>Why some twins were excluded ({readiness.twins_excluded})</span>
          {excludedExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" strokeWidth={1.75} /> : <ChevronDown className="h-4 w-4 text-slate-400" strokeWidth={1.75} />}
        </button>
        {excludedExpanded && (
          <div className="px-4 pb-4 text-sm text-slate-600 space-y-2 border-t border-border-gray">
            <p className="pt-3">Twins may be excluded from bulk queries for these reasons:</p>
            <ul className="space-y-1.5 ml-4 list-disc">
              <li><strong>Too few prediction responses</strong> — fewer than 3 answered prediction columns; insufficient signal for backtest.</li>
              <li><strong>Cost cap reached</strong> — plan-gated backtest budget exhausted; additional twins were not backtested.</li>
              <li><strong>All-identical responses</strong> — all of a respondent's answers were the same value; no discriminative signal.</li>
              <li><strong>Low confidence</strong> — Wilson lower below 0.4 after backtest; still visible in the twin list but excluded from bulk queries by default.</li>
            </ul>
            <p className="mt-2">You can include low-confidence twins in a bulk query by lowering the confidence floor in the composer.</p>
          </div>
        )}
      </div>

      {/* Which surface? expandable */}
      <div className="border border-border-gray rounded-xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-deep-ink hover:bg-soft-panel/50 transition-colors"
          onClick={() => setWhichSurfaceExpanded(!whichSurfaceExpanded)}
          aria-expanded={whichSurfaceExpanded}
        >
          <span>Which surface should I use — Echo Panel or Twin Panel?</span>
          {whichSurfaceExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" strokeWidth={1.75} /> : <ChevronDown className="h-4 w-4 text-slate-400" strokeWidth={1.75} />}
        </button>
        {whichSurfaceExpanded && (
          <div className="px-4 pb-4 border-t border-border-gray grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-deep-ink">Echo Panel (segments) is best when:</p>
              <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                <li>You want a fast directional pre-read on a new question</li>
                <li>Segment-level differences are the unit of analysis</li>
                <li>You haven't run the twin-building pipeline yet</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-deep-ink">Twin Panel (individuals) is best when:</p>
              <ul className="text-sm text-slate-600 space-y-1 ml-4 list-disc">
                <li>You want per-customer prediction with backtest-validated accuracy</li>
                <li>You need to defend a forecast to a sophisticated stakeholder</li>
                <li>You're delivering an engagement that requires 1:1 twin modeling</li>
                <li>You want to identify outlier respondents within a segment</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
